import { Controller } from '@hotwired/stimulus'

export type TooltipMode = 'dateTime' | 'date' | 'shortDateTime'

export default class TimestampController extends Controller<HTMLTimeElement> {
  static targets = ['display']
  static values = { tooltipMode: { default: 'time' } }

  declare readonly tooltipModeValue: TooltipMode

  connect() {
    var chunks: string[] = []

    switch (this.tooltipModeValue) {
      case 'date':
        chunks.push(this.getFormattedDate(this.element.dateTime))
        break
      case 'shortDateTime':
        chunks.push(this.getFormattedShortDateTime(this.element.dateTime))
        break
      default:
        chunks.push(this.getFormattedDateTime(this.element.dateTime))
    }

    const prefix = this.element.dataset.timestampPrefix || null

    if (prefix) chunks.unshift(prefix)

    this.element.setAttribute('aria-label', chunks.join(' '))
  }

  getFormattedShortDateTime(isoDateString: string) {
    const date = new Date(isoDateString)

    const datePart = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date)

    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
      .format(date)
      .replace(/\s(AM|PM)/, '$1')

    return `${datePart} ${timePart}`
  }

  /**
   * This method formats an ISO 8601 date string to a human-readable format
   * (e.g. "Wed, May 26, 2021)
   */
  getFormattedDate(isoDateString: string) {
    const date = new Date(isoDateString)
    const formattedDate = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date)

    return formattedDate
  }

  /**
   * This method formats an ISO 8601 date string to a human-readable format
   * (e.g. "Wed, May 26, 2021, 10:41:13 AM EDT")
   */
  getFormattedDateTime(isoDateString: string) {
    const date = new Date(isoDateString)
    const formattedDate = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      month: 'short',
      second: 'numeric',
      timeZoneName: 'short',
      weekday: 'short',
      year: 'numeric',
    }).format(date)

    return formattedDate
  }
}
